/**
 * expense-codes.ts — แคตตาล็อกโค้ดค่าใช้จ่ายสำเร็จรูป (Simple Mode)
 *
 * แนวคิด: ผู้ใช้ "เลือกโค้ด" (เช่น 16-Packing) แล้วระบบเติมค่าตั้งต้นของ
 * ฟอร์มกลางให้: หมวด P&L + ภาษี (หัก ณ ที่จ่าย/VAT) + การจัดประเภท
 * — โค้ดเป็นเพียง "หน้ากากที่เข้าใจง่าย" ของช่องใน financial_entries เดิม
 * ไม่มีการเปลี่ยนสูตร P&L (pnlCategory ใช้ค่าที่ CATEGORY_MAP ของ report รู้จัก)
 *
 * ค่าภาษีเป็นค่า "ตั้งต้น" อ้างอิงประมวลรัษฎากร (ดู docs/artifact ภาษี 41 โค้ด)
 * ผู้ใช้/ฝ่ายบัญชีแก้ต่อรายการได้เสมอ — ควรยืนยันกับผู้สอบบัญชีก่อนใช้จริง
 */
import { z } from 'zod';

/** หมวด P&L ที่ report รองรับ (ตรงกับ CATEGORY_MAP ใน apps/api/src/pnl/report.ts) */
export const PNL_CATEGORY = {
  MARKETING: 'Marketing expenses',
  COMMISSION: 'Commissions & platform fees',
  TRANSPORT: 'Transportation & fulfillment',
  PAYROLL: 'Payroll & employee expenses',
  PROFESSIONAL: 'Accounting & professional fees',
  FIXED: 'Fixed Cost',
  VARIABLE: 'Variable Cost',
  OTHER_OP: 'Other operating expense',
  INTEREST: 'Interest & finance costs',
} as const;

export type ExpenseFamily =
  | 'ส่งและแพ็ก'
  | 'การตลาด'
  | 'คอมมิชชั่น'
  | 'เงินเดือนและพนักงาน'
  | 'ค่าวิชาชีพ'
  | 'ค่าประจำ'
  | 'ค่าผันแปร'
  | 'ดำเนินงานอื่น'
  | 'ดอกเบี้ยและการเงิน'
  | 'บริจาค'
  | 'พิเศษ (ตรวจสอบก่อน)';

/** VAT treatment ของโค้ด → แปลงเป็น vatBasis/vatClaimability ตอน resolve */
export type CodeVat = 'claimable' | 'non_claimable' | 'exempt' | 'deemed' | 'pp36' | 'none';

export interface ExpenseCode {
  /** เลขโค้ดสั้น เช่น "16-Packing" (ใช้เป็น key ด้วย) */
  code: string;
  /** ชื่อไทยที่แม่ค้าเข้าใจ */
  name: string;
  family: ExpenseFamily;
  /** การจัดประเภทหลัก (ตรงกับ entryClassification) */
  classification: 'expense' | 'asset' | 'liability' | 'tax_only';
  /** หมวด P&L (เฉพาะ classification === 'expense'); undefined = ไม่เข้ากำไรขาดทุนตรง ๆ */
  pnlCategory?: string;
  /** อัตราหัก ณ ที่จ่าย % (1/2/3/5); undefined = ไม่หัก หรือคิดตามอัตราก้าวหน้า */
  whtRate?: number;
  /** ข้อความกำกับ WHT เช่น 'ก้าวหน้า (ภ.ง.ด.1)' */
  whtNote?: string;
  vat?: CodeVat;
  /** false = รายจ่ายต้องห้าม (ต้องบวกกลับตอนคำนวณภาษี) */
  taxDeductible?: boolean;
  /** ต้องตรวจก่อน (ทรัพย์สิน/หนี้สิน/ลูกหนี้) — ไม่ใช่ค่าใช้จ่ายปกติ */
  needsReview?: boolean;
  /** ข้อความเตือนพิเศษ (asset/liability/receivable/add-back) */
  warn?: string;
  /** หมายเหตุภาษีสั้น ๆ โชว์ตอน auto-fill */
  note?: string;
  /** หน้าที่ควรไปบันทึก (เฉพาะโค้ดที่ไม่ใช่ค่าใช้จ่ายปกติ); ไม่ตั้ง = เดาจาก classification */
  route?: SimpleTargetRoute;
}

/** หน้าใน Simple Mode ที่โค้ดพิเศษควรพาไป */
export type SimpleTargetRoute = 'assets' | 'payroll' | 'tax' | 'inventory' | 'entries';

const ROUTE_LABELS: Record<SimpleTargetRoute, string> = {
  assets: 'ทรัพย์สิน',
  payroll: 'เงินเดือน',
  tax: 'ภาษีและปรับปรุง',
  inventory: 'ซื้อสินค้าและสต็อก',
  entries: 'รายการรายรับ-รายจ่าย',
};

export interface CodeRedirect {
  route: SimpleTargetRoute;
  /** ชื่อหน้าปลายทาง (ไทย) */
  pageLabel: string;
  /** ชนิดรายการ (ทรัพย์สิน/หนี้สิน/รายการภาษี) */
  kindLabel: string;
}

/**
 * โค้ดที่ไม่ใช่ค่าใช้จ่ายปกติ (asset/liability/tax_only) ควรบันทึกที่หน้าไหน
 * @returns null ถ้าเป็นค่าใช้จ่ายปกติ (บันทึกในฟอร์มได้เลย)
 */
export function expenseCodeRedirect(c: ExpenseCode): CodeRedirect | null {
  if (c.classification === 'expense') return null;
  const route: SimpleTargetRoute = c.route ?? (c.classification === 'tax_only' ? 'tax' : 'assets');
  const kindLabel =
    c.classification === 'asset' ? 'ทรัพย์สิน' : c.classification === 'liability' ? 'หนี้สิน' : 'รายการภาษี';
  return { route, pageLabel: ROUTE_LABELS[route], kindLabel };
}

const C = PNL_CATEGORY;

/**
 * ชุดโค้ดสำเร็จรูป 41 โค้ดของบริษัท + โค้ดเสนอเพิ่ม (42-52) + แยกบริจาค 2 โค้ดย่อย
 * (10-Advance ย้ายเข้าเงินเดือนตามที่ผู้ใช้แจ้ง; 40 แยกเป็น 40a/40b)
 */
export const EXPENSE_CODE_CATALOG: ExpenseCode[] = [
  // ── ส่งและแพ็ก → Transportation & fulfillment ──
  { code: '16-Packing', name: 'ค่าอุปกรณ์แพ็คสินค้า', family: 'ส่งและแพ็ก', classification: 'expense', pnlCategory: C.TRANSPORT, vat: 'claimable', note: 'กล่อง/เทป/บับเบิ้ล' },
  { code: '23-Shipping', name: 'ค่าขนส่งสินค้า', family: 'ส่งและแพ็ก', classification: 'expense', pnlCategory: C.TRANSPORT, whtRate: 1, vat: 'exempt', note: 'ขนส่งเอกชน หัก 1% · ขนส่งในประเทศยกเว้น VAT' },
  { code: '30-Post', name: 'ค่าไปรษณีย์', family: 'ส่งและแพ็ก', classification: 'expense', pnlCategory: C.TRANSPORT, vat: 'exempt', note: 'ไปรษณีย์ไทย · ยกเว้น VAT · ไม่หัก' },

  // ── การตลาด → Marketing expenses ──
  { code: '18-Ads', name: 'ค่าโฆษณา', family: 'การตลาด', classification: 'expense', pnlCategory: C.MARKETING, vat: 'pp36', note: 'FB/Google: ไม่หัก · บริษัทยื่น ภ.พ.36 เอง · ในประเทศจ่ายเอเจนซี่หัก 2%' },
  { code: '17-Premium', name: 'ค่าของแถม', family: 'การตลาด', classification: 'expense', pnlCategory: C.MARKETING, vat: 'deemed', note: 'แจกของแถม → ขายถือว่าขาย (Deemed Sales) ต้องมี output VAT' },
  { code: '04-OffDecora', name: 'ค่าอุปกรณ์ตกแต่ง/พร้อพ Live', family: 'การตลาด', classification: 'expense', pnlCategory: C.MARKETING, vat: 'claimable', note: 'ของแต่งออฟฟิศ/ไลฟ์' },

  // ── คอมมิชชั่น → Commissions & platform fees ──
  { code: '28-Comm', name: 'ค่าคอมมิชชั่น', family: 'คอมมิชชั่น', classification: 'expense', pnlCategory: C.COMMISSION, whtRate: 3, whtNote: 'คนนอก (ม.40(2))', vat: 'claimable', note: 'คนนอกหัก 3% · พนักงาน→รวมเงินเดือน' },

  // ── เงินเดือนและพนักงาน → Payroll & employee expenses ──
  { code: '27-Salary', name: 'เงินเดือนพนักงาน', family: 'เงินเดือนและพนักงาน', classification: 'expense', pnlCategory: C.PAYROLL, whtNote: 'ก้าวหน้า (ภ.ง.ด.1)', vat: 'none', note: 'ม.40(1) → ภ.ง.ด.1' },
  { code: '09-Payroll', name: 'ค่าจ้างรายวัน', family: 'เงินเดือนและพนักงาน', classification: 'expense', pnlCategory: C.PAYROLL, whtRate: 3, whtNote: 'จ้างบุคคล (ลูกจ้าง=ก้าวหน้า)', vat: 'none', note: 'แนบบัตร ปชช.' },
  { code: '11-Bonus', name: 'เงินรางวัลพนักงาน', family: 'เงินเดือนและพนักงาน', classification: 'expense', pnlCategory: C.PAYROLL, whtNote: 'รวม ภ.ง.ด.1', vat: 'none', note: 'รวมคำนวณภาษีพนักงาน' },
  { code: '29-Allow', name: 'ค่าเบี้ยเลี้ยงพนักงาน', family: 'เงินเดือนและพนักงาน', classification: 'expense', pnlCategory: C.PAYROLL, vat: 'none', note: 'ต้องมีระเบียบบริษัทรองรับ' },
  { code: '26-SSO', name: 'เงินสมทบประกันสังคม', family: 'เงินเดือนและพนักงาน', classification: 'expense', pnlCategory: C.PAYROLL, vat: 'none', note: 'นำส่งภายในวันที่ 15' },
  { code: '49-Uniform', name: 'ค่าเครื่องแบบพนักงาน', family: 'เงินเดือนและพนักงาน', classification: 'expense', pnlCategory: C.PAYROLL, vat: 'claimable', note: 'สวัสดิการตามระเบียบ (จ้างตัด=หัก 3%)' },
  { code: '10-Advance', name: 'เบิกเงินเดือนล่วงหน้า (พนักงาน)', family: 'เงินเดือนและพนักงาน', classification: 'asset', needsReview: true, vat: 'none', route: 'payroll', warn: 'ตอนเบิกเป็น "ลูกหนี้/เงินทดรอง" ยังไม่เป็นค่าใช้จ่าย จะเป็นค่าใช้จ่ายเมื่อหักกลบเงินเดือนงวดนั้น', note: 'ลูกหนี้เงินยืมพนักงาน' },

  // ── ค่าวิชาชีพ → Accounting & professional fees ──
  { code: '38-AuditFee', name: 'ค่าทำบัญชี/สอบบัญชี', family: 'ค่าวิชาชีพ', classification: 'expense', pnlCategory: C.PROFESSIONAL, whtRate: 3, whtNote: 'วิชาชีพ (ม.40(6))', vat: 'claimable', note: 'หัก 3% ค่าวิชาชีพ' },
  { code: '45-Consulting', name: 'ค่าที่ปรึกษา/กฎหมาย', family: 'ค่าวิชาชีพ', classification: 'expense', pnlCategory: C.PROFESSIONAL, whtRate: 3, whtNote: 'วิชาชีพ/บริการ', vat: 'claimable', note: 'ที่ปรึกษา/ทนาย/วิศวกร หัก 3%' },

  // ── ค่าประจำ → Fixed Cost ──
  { code: '36-OffRent', name: 'ค่าเช่าออฟฟิศ/โกดัง', family: 'ค่าประจำ', classification: 'expense', pnlCategory: C.FIXED, whtRate: 5, whtNote: 'เช่าอสังหาฯ (ม.40(5))', vat: 'exempt', note: 'สำคัญ: หัก 5% แต่เช่าอสังหาฯ ยกเว้น VAT' },
  { code: '37-Utility', name: 'ค่าน้ำ/ค่าไฟ', family: 'ค่าประจำ', classification: 'expense', pnlCategory: C.FIXED, vat: 'claimable', note: 'บิลชื่อบริษัท ขอคืน VAT ได้' },
  { code: '12-Internet', name: 'ค่าอินเตอร์เน็ต', family: 'ค่าประจำ', classification: 'expense', pnlCategory: C.FIXED, whtRate: 3, whtNote: 'บริการรายเดือน', vat: 'claimable', note: 'เติมเงินไม่ต้องหัก' },
  { code: '13-SimNet', name: 'ค่าโทรศัพท์/ซิม', family: 'ค่าประจำ', classification: 'expense', pnlCategory: C.FIXED, whtRate: 3, whtNote: 'บริการรายเดือน', vat: 'claimable', note: 'เติมเงินไม่ต้องหัก' },
  { code: '20-Software', name: 'ค่า Application/Software', family: 'ค่าประจำ', classification: 'expense', pnlCategory: C.FIXED, whtRate: 3, whtNote: 'ในประเทศ · ตปท.→ภ.พ.36', vat: 'pp36', note: 'Cloud ตปท. (AWS/Azure/Google) → ยื่น ภ.พ.36' },
  { code: '44-Insurance', name: 'เบี้ยประกันภัย', family: 'ค่าประจำ', classification: 'expense', pnlCategory: C.FIXED, vat: 'none', note: 'ประกันทรัพย์สิน/ความรับผิด/ขนส่ง' },
  { code: '43-BankFee', name: 'ค่าธรรมเนียมธนาคาร', family: 'ค่าประจำ', classification: 'expense', pnlCategory: C.FIXED, vat: 'none', note: 'ค่าโอน/รักษาบัญชี/EDC · ธนาคารมักยกเว้น VAT' },

  // ── ค่าผันแปร → Variable Cost ──
  { code: '05-OffSupply', name: 'ของใช้สำนักงาน', family: 'ค่าผันแปร', classification: 'expense', pnlCategory: C.VARIABLE, vat: 'claimable', note: 'ใช้แล้วหมดไป' },
  { code: '21-Makeup', name: 'วัสดุสิ้นเปลือง', family: 'ค่าผันแปร', classification: 'expense', pnlCategory: C.VARIABLE, vat: 'claimable', note: 'ของกองกลาง' },
  { code: '25-Repair', name: 'ค่าซ่อมแซม', family: 'ค่าผันแปร', classification: 'expense', pnlCategory: C.VARIABLE, whtRate: 3, whtNote: 'บริการ', vat: 'claimable', note: 'ซ่อมรถยนต์นั่ง≤10 → VAT ต้องห้าม' },
  { code: '07-Fuel', name: 'ค่าน้ำมันเชื้อเพลิง', family: 'ค่าผันแปร', classification: 'expense', pnlCategory: C.VARIABLE, vat: 'claimable', note: 'ระบุทะเบียนรถบริษัท · รถยนต์นั่ง≤10 → VAT ต้องห้าม' },
  { code: '06-CarRent', name: 'ค่าเช่ารถ (ขับเอง)', family: 'ค่าผันแปร', classification: 'expense', pnlCategory: C.VARIABLE, whtRate: 5, whtNote: 'เช่าทรัพย์สิน', vat: 'non_claimable', note: 'รถยนต์นั่ง≤10 → VAT ต้องห้าม' },
  { code: '33-VanRent', name: 'เช่ารถพร้อมคนขับ', family: 'ค่าผันแปร', classification: 'expense', pnlCategory: C.VARIABLE, whtRate: 3, whtNote: 'บริการรับจ้าง', vat: 'claimable', note: 'มีคนขับ = บริการ ไม่ใช่เช่า' },
  { code: '34-RentExec', name: 'เช่ารถผู้บริหาร', family: 'ค่าผันแปร', classification: 'expense', pnlCategory: C.VARIABLE, whtRate: 5, whtNote: 'เช่าทรัพย์สิน', vat: 'non_claimable', note: 'ต้องมีสัญญาเช่า · รถยนต์นั่ง VAT ต้องห้าม' },
  { code: '14-ExecExp', name: 'ค่าของใช้ผู้บริหาร', family: 'ค่าผันแปร', classification: 'expense', pnlCategory: C.VARIABLE, vat: 'claimable', warn: 'แยกเรื่องงานกับส่วนตัวให้ชัด — ส่วนตัวเป็นรายจ่ายต้องห้าม', note: 'ม.65 ตรี(3)' },
  { code: '15-StaffUse', name: 'ค่าของใช้พนักงาน', family: 'ค่าผันแปร', classification: 'expense', pnlCategory: C.VARIABLE, vat: 'claimable', note: 'จิปาถะทั่วไป' },
  { code: '46-Royalty', name: 'ค่าลิขสิทธิ์/ค่าสิทธิ์', family: 'ค่าผันแปร', classification: 'expense', pnlCategory: C.VARIABLE, whtRate: 3, whtNote: 'ในประเทศ (ม.40(3)) · ตปท. ม.70', vat: 'claimable', note: 'ต่างประเทศ + ภ.พ.36/อนุสัญญาภาษีซ้อน' },

  // ── ดำเนินงานอื่น → Other operating expense ──
  { code: '01-F&B', name: 'อาหารและเครื่องดื่ม', family: 'ดำเนินงานอื่น', classification: 'expense', pnlCategory: C.OTHER_OP, vat: 'claimable', note: 'เลี้ยงพนักงาน=สวัสดิการ · รับรองลูกค้า→ใช้ 31' },
  { code: '31-Entertain', name: 'ค่ารับรอง', family: 'ดำเนินงานอื่น', classification: 'expense', pnlCategory: C.OTHER_OP, vat: 'non_claimable', note: 'หักได้ ≤0.3% รายได้ (สูงสุด 10 ล้าน) · VAT ต้องห้าม' },
  { code: '02-AirTicket', name: 'ค่าตั๋วเครื่องบิน', family: 'ดำเนินงานอื่น', classification: 'expense', pnlCategory: C.OTHER_OP, vat: 'claimable', note: 'ซื้อตรงสายการบิน = ไม่หัก (ขนส่งสาธารณะ)' },
  { code: '03-Hotel', name: 'ค่าที่พัก', family: 'ดำเนินงานอื่น', classification: 'expense', pnlCategory: C.OTHER_OP, vat: 'claimable', note: 'ใบกำกับชื่อบริษัท' },
  { code: '08-Grab', name: 'ค่า Grab (เดินทาง)', family: 'ดำเนินงานอื่น', classification: 'expense', pnlCategory: C.OTHER_OP, vat: 'none', note: 'e-Receipt' },
  { code: '24-Medical', name: 'ค่ารักษาพยาบาล', family: 'ดำเนินงานอื่น', classification: 'expense', pnlCategory: C.OTHER_OP, vat: 'exempt', note: 'สวัสดิการ (ใบรับรองแพทย์) · สถานพยาบาลยกเว้น VAT' },
  { code: '39-Training', name: 'ค่าอบรมสัมมนา', family: 'ดำเนินงานอื่น', classification: 'expense', pnlCategory: C.OTHER_OP, whtRate: 3, whtNote: 'บริการ', vat: 'claimable', note: 'หักได้ 200% (เงื่อนไข พ.ร.บ.ส่งเสริมฯ 2545)' },
  { code: '22-Tax', name: 'ค่าใช้จ่ายทางภาษี', family: 'ดำเนินงานอื่น', classification: 'expense', pnlCategory: C.OTHER_OP, vat: 'none', note: 'ภาษีป้าย/อากรแสตมป์ (ไม่มี VAT)' },
  { code: '19-Misc', name: 'ค่าใช้จ่ายเบ็ดเตล็ด', family: 'ดำเนินงานอื่น', classification: 'expense', pnlCategory: C.OTHER_OP, note: 'ระบุหมวดไม่ได้' },
  { code: '47-BadDebt', name: 'หนี้สูญ', family: 'ดำเนินงานอื่น', classification: 'expense', pnlCategory: C.OTHER_OP, vat: 'none', note: 'ตัดได้ตามเงื่อนไข ม.65 ทวิ(9) + กฎกระทรวง 186' },
  { code: '50-GovFee', name: 'ค่าธรรมเนียมราชการ', family: 'ดำเนินงานอื่น', classification: 'expense', pnlCategory: C.OTHER_OP, vat: 'none', note: 'จดทะเบียน/ค่าธรรมเนียมภาครัฐ · ไม่มี VAT' },
  { code: '51-NonClaimVat', name: 'ภาษีซื้อขอคืนไม่ได้', family: 'ดำเนินงานอื่น', classification: 'expense', pnlCategory: C.OTHER_OP, vat: 'none', note: 'VAT ที่เครดิตไม่ได้ → ลงเป็นรายจ่าย' },
  { code: '52-FxDiff', name: 'ผลต่างอัตราแลกเปลี่ยน', family: 'ดำเนินงานอื่น', classification: 'expense', pnlCategory: C.OTHER_OP, vat: 'none', note: 'ขาดทุน/กำไร FX (ม.65 ทวิ(5))' },
  { code: '41-Other', name: 'อื่นๆ (สำรอง)', family: 'ดำเนินงานอื่น', classification: 'expense', pnlCategory: C.OTHER_OP, note: 'รายการตกหล่น — เลือกภาษีเอง' },

  // ── ดอกเบี้ยและการเงิน → Interest & finance costs ──
  { code: '42-Interest', name: 'ดอกเบี้ยเงินกู้', family: 'ดอกเบี้ยและการเงิน', classification: 'expense', pnlCategory: C.INTEREST, vat: 'none', note: 'กู้เพื่อกิจการ · สถาบันการเงินไม่หัก (บุคคล/บริษัทอื่นหัก 1%)' },

  // ── บริจาค (แยก 2 โค้ดย่อยเพื่อคุมเพดานภาษี) → Other operating ──
  { code: '40a-DonationX2', name: 'บริจาค รพ.รัฐ/สถานศึกษา (e-Donation)', family: 'บริจาค', classification: 'expense', pnlCategory: C.OTHER_OP, vat: 'none', note: 'หัก 2 เท่า รวมไม่เกิน 10% กำไรสุทธิ · ต้องผ่าน e-Donation' },
  { code: '40b-DonationX1', name: 'บริจาค วัด/กุศลทั่วไป', family: 'บริจาค', classification: 'expense', pnlCategory: C.OTHER_OP, vat: 'none', note: 'หัก 1 เท่า ไม่เกิน 2% กำไรสุทธิ' },

  // ── พิเศษ: ไม่ใช่ค่าใช้จ่าย / บวกกลับ ──
  { code: '32-Asset', name: 'ทรัพย์สิน (คอม/มือถือ)', family: 'พิเศษ (ตรวจสอบก่อน)', classification: 'asset', needsReview: true, vat: 'claimable', warn: 'นี่คือทรัพย์สิน — ห้ามลงเป็นค่าใช้จ่าย ควรลงหน้า "ทรัพย์สิน" เพื่อคิดค่าเสื่อม', note: 'พ.ร.ฎ.145 · รถยนต์นั่ง VAT ต้องห้าม' },
  { code: '35-Leasing', name: 'เช่าซื้อรถ (ผ่อนไฟแนนซ์)', family: 'พิเศษ (ตรวจสอบก่อน)', classification: 'liability', needsReview: true, warn: 'เงินต้นเป็นหนี้สิน — ห้ามลงเป็นค่าใช้จ่าย (ดอกเบี้ยลงได้ · ทรัพย์สินคิดค่าเสื่อม)', note: 'ตัดชำระหนี้สิน' },
  { code: '48-Penalty', name: 'ค่าปรับ/เงินเพิ่มภาษี', family: 'พิเศษ (ตรวจสอบก่อน)', classification: 'expense', pnlCategory: C.OTHER_OP, taxDeductible: false, vat: 'none', warn: 'รายจ่ายต้องห้าม (ม.65 ตรี(6)) — บันทึกได้แต่ต้องบวกกลับ ไม่ลดกำไรทางภาษี', note: 'บวกกลับตอนคำนวณภาษี' },
];

/** ค้นโค้ดจากรหัส */
export function findExpenseCode(
  code: string,
  extra: ExpenseCode[] = [],
): ExpenseCode | undefined {
  return [...EXPENSE_CODE_CATALOG, ...extra].find((c) => c.code === code);
}

/** ค่าตั้งต้นของฟอร์ม entry ที่ resolve จากโค้ด (merge ทับด้วยยอด/วันที่ทีหลัง) */
export interface CodeEntryDefaults {
  classification: 'expense' | 'asset' | 'liability' | 'tax_only';
  pnlCategory?: string;
  whtRate?: number;
  vatBasis?: 'exempt';
  vatClaimability?: 'claimable' | 'non_claimable' | 'na';
  taxDeductible?: boolean;
}

/**
 * แปลงโค้ด → ค่าตั้งต้นของช่องใน financial_entries
 * (pure — ใช้ทั้ง web สร้าง body และ test)
 */
export function resolveCodeDefaults(c: ExpenseCode): CodeEntryDefaults {
  const out: CodeEntryDefaults = { classification: c.classification };
  if (c.pnlCategory) out.pnlCategory = c.pnlCategory;
  if (c.whtRate !== undefined) out.whtRate = c.whtRate;
  if (c.taxDeductible === false) out.taxDeductible = false;
  switch (c.vat) {
    case 'claimable':
      out.vatClaimability = 'claimable';
      break;
    case 'non_claimable':
      out.vatClaimability = 'non_claimable';
      break;
    case 'exempt':
      out.vatBasis = 'exempt';
      out.vatClaimability = 'na';
      break;
    case 'deemed':
    case 'pp36':
    case 'none':
      out.vatClaimability = 'na';
      break;
    default:
      break;
  }
  return out;
}

// ─────────────────────────────────────────────────────────────
// โค้ดที่ผู้ใช้เพิ่มเอง (custom) — เก็บใน backend (ตาราง custom_expense_codes)
// ─────────────────────────────────────────────────────────────

/** input สำหรับบันทึกโค้ดที่ผู้ใช้เพิ่มเอง (validate ทั้ง web/api) */
export const customExpenseCodeInput = z.object({
  code: z.string().trim().min(1, 'ต้องมีรหัสโค้ด').max(80),
  name: z.string().trim().min(1, 'ตั้งชื่อโค้ด').max(120),
  family: z.string().trim().min(1).max(60).optional(),
  classification: z.enum(['expense', 'asset', 'liability', 'tax_only']).optional(),
  pnlCategory: z.string().trim().max(120).optional(),
  whtRate: z.number().min(0).max(100).optional(),
  vat: z.string().trim().max(20).optional(),
  note: z.string().trim().max(200).optional(),
});
export type CustomExpenseCodeInput = z.infer<typeof customExpenseCodeInput>;

/** แปลง row จาก DB (snake_case) → ExpenseCode สำหรับ UI */
export function rowToExpenseCode(row: {
  code: string;
  name: string;
  family?: string | null;
  classification?: string | null;
  pnl_category?: string | null;
  wht_rate?: number | string | null;
  vat?: string | null;
  note?: string | null;
}): ExpenseCode {
  const wht = row.wht_rate == null ? undefined : Number(row.wht_rate);
  return {
    code: row.code,
    name: row.name,
    family: (row.family ?? 'ดำเนินงานอื่น') as ExpenseFamily,
    classification: (row.classification ?? 'expense') as ExpenseCode['classification'],
    ...(row.pnl_category ? { pnlCategory: row.pnl_category } : {}),
    ...(wht !== undefined && Number.isFinite(wht) ? { whtRate: wht } : {}),
    ...(row.vat ? { vat: row.vat as CodeVat } : {}),
    note: row.note ?? 'โค้ดที่เพิ่มเอง',
  };
}
